/**
 * Everything Krevya writes to a visitor's browser, in one list.
 *
 * The consent store reads it to know which keys are necessary and which are
 * preferences, and the Cookie Policy's table is built from it, so the policy
 * and the code cannot describe two different sets of keys. A unit test
 * (`test/storage-inventory.test.ts`) reads the source for every `krevya` key
 * and every cookie the API sets and fails when one is missing here, or when a
 * name here no longer exists in the code.
 *
 * A name ending in `*` is a prefix: `krevya.step.*` is one key per project.
 */

export type StorageCategory = 'necessary' | 'preferences' | 'analytics';

/** Where a row lives. `preference` means localStorage with consent, sessionStorage (or memory) without. */
export type StorageMedium = 'cookie' | 'local' | 'session' | 'preference' | 'cookie+local' | 'posthog';

export type StorageRow = {
  id: StorageRowId;
  names: readonly string[];
  medium: StorageMedium;
  category: StorageCategory;
  /** Who writes it and who receives it. */
  provider: 'krevya' | 'posthog';
};

export type StorageRowId =
  | 'session'
  | 'twoFactor'
  | 'admin'
  | 'consent'
  | 'tabState'
  | 'display'
  | 'currency'
  | 'uiFlags'
  | 'posthog';

export const STORAGE_INVENTORY: readonly StorageRow[] = [
  { id: 'session', names: ['__Secure-krevya.session_token'], medium: 'cookie', category: 'necessary', provider: 'krevya' },
  { id: 'twoFactor', names: ['__Secure-krevya.two_factor'], medium: 'cookie', category: 'necessary', provider: 'krevya' },
  { id: 'admin', names: ['__Host-krevya_admin'], medium: 'cookie', category: 'necessary', provider: 'krevya' },
  { id: 'consent', names: ['krevya_consent'], medium: 'cookie+local', category: 'necessary', provider: 'krevya' },
  {
    id: 'tabState',
    names: [
      'krevya.promo',
      'krevya.connect.returnTo',
      'krevya.onboarding.syncStarted',
      'krevya.desktopGate.dismissed',
      'krevya.landing.ctaBarDismissed',
    ],
    medium: 'session',
    category: 'necessary',
    provider: 'krevya',
  },
  { id: 'display', names: ['krevya.locale', 'krevya.region'], medium: 'preference', category: 'preferences', provider: 'krevya' },
  { id: 'currency', names: ['krevya_currency'], medium: 'cookie', category: 'preferences', provider: 'krevya' },
  {
    id: 'uiFlags',
    names: [
      'krevya.nav.folded',
      'krevya.studio.tips.hidden',
      'krevya.step.*',
      'krevya.imagePrompts.*',
      'krevya.ads.sections.*',
      'krevya.coverPalette.asked',
      'krevya.landing.guideSeen',
      'krevya.landing.offerSeen',
      'krevya.offer',
    ],
    medium: 'preference',
    category: 'preferences',
    provider: 'krevya',
  },
  { id: 'posthog', names: ['ph_*', '__ph_*'], medium: 'posthog', category: 'analytics', provider: 'posthog' },
];

function namesWhere(test: (row: StorageRow) => boolean): string[] {
  return STORAGE_INVENTORY.filter(test).flatMap((row) => [...row.names]);
}

/** The sessionStorage keys that are necessary and tab-scoped by design; never promoted to localStorage. */
export const SESSION_ONLY_KEYS: readonly string[] = namesWhere(
  (row) => row.medium === 'session' && row.category === 'necessary',
);
